import { useVyStepper } from '../../../../../../../../hooks/use-vy-stepper';
import {
  AUTO_RENEW_CONTRACT_STEPS,
  AutoRenewContractStep,
} from '../renew-contract-dialog-types/auto-renew-contract-steps';
import {
  RenewContractStepFlowConfig,
  RenewContractStepViewConfig,
} from '../renew-contract-dialog-types/renew-contract-step-view-config';
import { AutoRenewContractPreferences } from '../renew-contract-dialog-components/AutoRenewContractPreferences';
import { useSetContractAutoRenewal } from '../../../../../../../../hooks/requests/use-set-contract-auto-renewal';
import { runMutation } from '../../../../../../../../utils/vy-mutation';
import { UseMutationResult } from 'react-query';
import { SetContractAutoRenewalRequestPayload } from '../../../../../../../../../../../libs/api-definition/src/api-definition-paths/contract-paths/set-contract-auto-renewal-path';
import { getOptionalObjectProperty, Optional } from '@vy/utils';
import { TurnOffAutoRenewal } from '../renew-contract-dialog-components/TurnOffAutoRenewal';

export function useAutoRenewContractFlow(
  contractId: string,
  isAutoRenewalChecked: boolean,
  handleClose?: () => void,
): RenewContractStepFlowConfig {
  const stepper = useVyStepper<AutoRenewContractStep>(
    AUTO_RENEW_CONTRACT_STEPS.Preferences,
    AUTO_RENEW_CONTRACT_STEPS,
  );
  const setContractAutoRenewalMutation = useSetContractAutoRenewal(contractId);

  const stepViewMap: Record<AutoRenewContractStep, RenewContractStepViewConfig> = {
    [AUTO_RENEW_CONTRACT_STEPS.Preferences]: {
      label: getAutoRenewalLabel(isAutoRenewalChecked),
      labelDescription: getAutoRenewalLabelDescription(isAutoRenewalChecked),
      ctaText: 'Confirm',
      ctaDisabled: setContractAutoRenewalMutation.isLoading,
      onNext: async () => {
        await handleAutoRenewalToggle(setContractAutoRenewalMutation, isAutoRenewalChecked, handleClose);
      },
      content: isAutoRenewalChecked ? <AutoRenewContractPreferences /> : <TurnOffAutoRenewal />,
    },
  };

  const getStepView = (step: AutoRenewContractStep): Optional<RenewContractStepViewConfig> => {
    return getOptionalObjectProperty(stepViewMap, step);
  };

  const goToPreviousStep = () => stepper.previousStep();

  return {
    getStepView,
    isFirstStep: stepper.isFirstStep,
    currentStep: stepper.currentStep,
    goToPreviousStep,
  };
}

async function handleAutoRenewalToggle(
  setContractAutoRenewalMutation: UseMutationResult<void, unknown, SetContractAutoRenewalRequestPayload>,
  isAutoRenewal: boolean,
  handleClose?: () => void,
) {
  await runMutation(
    setContractAutoRenewalMutation,
    {
      autoRenewal: isAutoRenewal,
    },
    {
      pending: 'Updating...',
      success: isAutoRenewal ? 'Contract is now auto-renew' : 'Auto-renew is now cancelled',
      error: 'Failed to update contract auto renewal',
    },
    {
      onSuccess: () => {
        handleClose?.();
      },
    },
  );
}

function getAutoRenewalLabel(isAutoRenewalChecked: boolean) {
  return isAutoRenewalChecked ? 'Auto-renewal options' : 'Contract auto-renewal';
}

function getAutoRenewalLabelDescription(isAutoRenewalChecked: boolean) {
  return isAutoRenewalChecked ? "Decide what you'd like to carry over into the renewed contract" : undefined;
}
